import { createForm } from "@tanstack/solid-form"
import { createFileRoute, redirect } from "@tanstack/solid-router"
import { For } from "solid-js"
import { z } from "zod"
import { Button } from "~/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "~/components/ui/card"
import { TextField, TextFieldErrorMessage, TextFieldInput, TextFieldLabel } from "~/components/ui/text-field"
import { authClient } from "~/lib/auth/client"

export const Route = createFileRoute("/_authenticated/tablet/create")({
  component: RouteComponent
})

function RouteComponent() {
  const form = createForm(() => ({
    defaultValues: {
      organizationName: "",
      organizationSlug: ""
    },
    validators: {
      onChange: z.object({
        organizationName: z.string().min(3, { message: "Name must be at least 3 characters long" }),
        organizationSlug: z
          .string()
          .min(3, { message: "Slug must be at least 3 characters long" })
          .regex(/^[a-z0-9-]+$/, {
            message: "Slug may only contain lowercase letters (a–z) and hyphens (-)"
          })
          .regex(/^(?!-)(?!.*--).*?(?<!-)$/, {
            message: "Slug cannot start or end with '-' and cannot contain consecutive '--'"
          })
      })
    },
    onSubmit: async ({ value }) => {
      const { data, error } = await authClient.organization.create({
        name: value.organizationName,
        slug: value.organizationSlug,
        metadata: {
          status: "creating"
        }
      })

      if (error) {
        console.error(error)
      } else {
        throw redirect({ to: "/tablet/$orgId", params: { orgId: data.id } })
      }
    }
  }))

  return (
    <div class="flex min-h-screen items-center justify-center">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <Card>
          <CardHeader>
            <CardTitle class="text-center">Create Organization</CardTitle>
          </CardHeader>
          <CardContent class="min-w-96 space-y-2">
            <form.Field
              name="organizationName"
              children={(field) => (
                <TextField
                  name={field().name}
                  value={field().state.value}
                  onBlur={field().handleBlur}
                  onChange={(v) => {
                    field().handleChange(v)
                    const slug = v
                      .trim()
                      .replace(/\s+/g, "-")
                      .replace(/-+/g, "-")
                      .replace(/^-+|-+$/g, "")
                      .toLowerCase()
                    field().form.setFieldValue("organizationSlug", slug)
                  }}
                  class="space-y-1"
                >
                  <TextFieldLabel>Name</TextFieldLabel>
                  <TextFieldInput type="text" />
                </TextField>
              )}
            />
            <form.Field
              name="organizationSlug"
              validators={{
                onChangeAsync: async ({ value, fieldApi, signal }) => {
                  const validSlug = await authClient.organization.checkSlug({
                    slug: value,
                    fetchOptions: {
                      signal
                    }
                  })

                  if (validSlug.error) {
                    return { message: validSlug.error.message }
                  }
                }
              }}
              children={(field) => (
                <TextField
                  name={field().name}
                  value={field().state.value}
                  onBlur={field().handleBlur}
                  onChange={(v) => field().handleChange(v)}
                  validationState={field().state.meta.isValid ? "valid" : "invalid"}
                  class="space-y-1"
                >
                  <TextFieldLabel>Slug</TextFieldLabel>
                  <TextFieldInput type="text" />
                  <For each={field().state.meta.errors}>
                    {(error) => <TextFieldErrorMessage>{error?.message}</TextFieldErrorMessage>}
                  </For>
                </TextField>
              )}
            />
          </CardContent>
          <CardFooter class="justify-end">
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting
              })}
              children={(state) => (
                <Button type="submit" disabled={!state().canSubmit}>
                  {state().isSubmitting ? "..." : "Submit"}
                </Button>
              )}
            />
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}
